import {
  shopeeVideoDurationMax,
  shopeeVideoSizeLimit,
  shopeeVideoDurationMin,
  lazadaVideoSizeLimit,
  lazadaVideoDurationMax,
  lazadaVideoMinWidth,
  whatsAppVideoSizeLimit,
  lazadaVideoDurationMin,
  facebookVideoSizeLimit,
  facebookVideoDurationMax,
  tiktokVideoSizeLimit,
} from 'utils/constants';
import { v4 as uuidV4 } from 'uuid';
import { tolgee } from 'services/i18n/tolgee';
import { isTiKTok } from 'lib/utils/isTikTok';
import posthog from '@leyan/lytics';

export const getVideoMetadata = (
  url: string,
): Promise<{
  duration: number;
  width: number;
  height: number;
}> => {
  return new Promise((resolve) => {
    const video = document.createElement('video');
    const id = uuidV4();
    video.setAttribute('id', id);
    video.style.position = 'fixed';
    video.style.top = '-9999px';
    video.style.left = '-9999px';
    video.src = url;
    document.body.append(video);

    const cleanup = () => {
      video.removeEventListener('loadedmetadata', onLoadedmetadata);
      video.src = '';
      document.body.removeChild(video);
    };

    const onLoadedmetadata = () => {
      const { videoWidth, duration, videoHeight } = video;
      cleanup();
      resolve({
        duration,
        width: videoWidth,
        height: videoHeight,
      });
    };
    video.addEventListener('loadedmetadata', onLoadedmetadata);
  });
};

export const getVideoThumbUrl = (url: string, seekTo = 0) => {
  return new Promise<string>((resolve, reject) => {
    const video = document.createElement('video');

    video.setAttribute('src', url);
    video.setAttribute('crossOrigin', 'anonymous');

    const cleanup = () => {
      video.removeEventListener('error', onError);
      video.removeEventListener('loadedmetadata', onLoadedmetadata);
      video.src = '';
    };

    const onError = () => {
      cleanup();
      reject();
    };

    const onLoadedmetadata = () => {
      const { videoWidth, videoHeight } = video;

      const onSeeked = () => {
        const canvas = document.createElement('canvas');
        canvas.width = videoWidth;
        canvas.height = videoHeight;

        const ctx = canvas.getContext('2d');

        if (!ctx) {
          cleanup();
          video.removeEventListener('seeked', onSeeked);
          reject();
          return;
        }

        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        cleanup();
        video.removeEventListener('seeked', onSeeked);
        resolve(ctx.canvas.toDataURL('image/jpeg'));
      };

      video.addEventListener('seeked', onSeeked);
      video.currentTime = seekTo;
    };

    video.addEventListener('error', onError);
    video.addEventListener('loadedmetadata', onLoadedmetadata);
    video.load();
  });
};

const validatorVideo = async (
  platform: General.PlatformUpperCaseTunnel | undefined,
  file: File,
  shopeeDuration = [shopeeVideoDurationMin, shopeeVideoDurationMax],
) => {
  const size = (file.size / 1024 / 1024).toFixed(2);
  if (!file.type?.startsWith('video')) {
    return {
      errorText: tolgee.t('ViewsChat.视频格式错误'),
      duration: -1,
      size,
      width: 0,
      height: 0,
    };
  }
  const url = URL.createObjectURL(file);

  const { duration, width, height } = await getVideoMetadata(url).finally(() => {
    URL.revokeObjectURL(url);
  });

  const [shopeeVideoDurationMin, shopeeVideoDurationMax] = shopeeDuration;

  posthog.capture('$videoMetadata', {
    platform: platform || 'UNKNOWN',
    duration,
    width,
    height,
    size,
  });

  // 多平台共用资源，需要按（Shopee｜Lazada | Facebook）的最严格要求检查
  if (!platform) {
    if (file.size > facebookVideoSizeLimit) {
      return {
        errorText: tolgee.t('ViewsChat.shopee视频体积超过限制').replace('30', '25'),
        duration,
        size,
        width,
        height,
      };
    }

    if (
      duration >
        Math.min(shopeeVideoDurationMax, lazadaVideoDurationMax, facebookVideoDurationMax) ||
      duration < Math.max(shopeeVideoDurationMin, lazadaVideoDurationMin)
    ) {
      return {
        errorText: tolgee.t('ViewsChat.shopee视频时常超过限制'),
        duration,
        size,
        width,
        height,
      };
    }

    if (width && height && Math.min(width, height) < lazadaVideoMinWidth) {
      return {
        errorText: tolgee.t('ViewsChat.lazada视频分辨率过低'),
        duration,
        size,
        width,
        height,
      };
    }
  }

  if (platform === 'SHOPEE') {
    if (file.size > shopeeVideoSizeLimit) {
      return {
        errorText: tolgee.t('ViewsChat.shopee视频体积超过限制'),
        duration,
        size,
        width,
        height,
      };
    }
    if (duration > shopeeVideoDurationMax || duration < shopeeVideoDurationMin) {
      return {
        errorText: tolgee.t('ViewsChat.shopee视频时常超过限制'),
        duration,
        size,
        width,
        height,
      };
    }
  } else if (platform === 'LAZADA') {
    if (file.size > lazadaVideoSizeLimit) {
      return {
        errorText: tolgee.t('ViewsChat.lazada视频体积超过限制'),
        duration,
        size,
        width,
        height,
      };
    }
    if (duration > lazadaVideoDurationMax) {
      return {
        errorText: tolgee.t('ViewsChat.lazada视频时常超过限制'),
        duration,
        size,
        width,
        height,
      };
    }
    if (width && height && Math.min(width, height) < lazadaVideoMinWidth) {
      return {
        errorText: tolgee.t('ViewsChat.lazada视频分辨率过低'),
        duration,
        size,
        width,
        height,
      };
    }
  } else if (platform === 'WA_BIZ') {
    if (file.size > whatsAppVideoSizeLimit) {
      return {
        errorText: tolgee.t('ViewsChat.whatsApp视频体积超过限制'),
        duration,
        size,
        width,
        height,
      };
    }
  } else if (platform === 'FACEBOOK') {
    if (file.size > facebookVideoSizeLimit) {
      return {
        errorText: tolgee.t('ViewsChat.shopee视频体积超过限制').replace('30', '25'),
        duration,
        size,
        width,
        height,
      };
    }
    if (duration > facebookVideoDurationMax) {
      return {
        errorText: tolgee.t('ViewsChat.lazada视频时常超过限制').replace('180', '75'),
        duration,
        size,
        width,
        height,
      };
    }
  } else if (isTiKTok(platform)) {
    if (file.size > tiktokVideoSizeLimit) {
      return {
        errorText: tolgee.t('ViewsChat.shopee视频体积超过限制').replace('30', '30'),
        duration,
        size,
        width,
        height,
      };
    }
  }
  return {
    errorText: '',
    duration,
    size,
    width,
    height,
  };
};

export default validatorVideo;
